import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import { VERIFF_CODES } from '@/helpers/constants';
import { prettyFloatMoney } from '@/helpers/utils';
import { useVeriffStatus } from '@/hooks/shared/useVeriffStatus';
import { useGetInvestingStatus } from '@/query/queries/investing/useGetInvestingStatus';
import { BonusForVerification } from './main';

const LOCAL_STORAGE_KEY = 'verification_success_modal_show';

const getBonusProps = ({ ready, veriffCode, investingStatus, t }) => {
  if (!ready || !investingStatus || !veriffCode) return null;

  const {
    is_participating,
    has_previously_invested,
    verification_bonus_unlock_remain_amount,
    verification_bonus_min_investment_amount,
    pending_verification_bonus_amount,
    received_verification_bonus_amount,
  } = investingStatus;

  if (veriffCode !== VERIFF_CODES.verified) {
    return Number(pending_verification_bonus_amount) ? {
      title: t('bonus_for_verification.title_with_amount', {
        amount: prettyFloatMoney(pending_verification_bonus_amount),
      }),
      badge: null,
      desc: t('bonus_for_verification.desc_verify', {
        amount: prettyFloatMoney(pending_verification_bonus_amount),
      }),
    } : null;
  }

  const isExistingInvestor = has_previously_invested || is_participating;

  if (!isExistingInvestor) {
    return Number(received_verification_bonus_amount) ? {
      title: t('bonus_for_verification.title_with_amount', {
        amount: prettyFloatMoney(received_verification_bonus_amount),
      }),
      badge: t('bonus_for_verification.badge_ready'),
      desc: t('bonus_for_verification.desc_congrats', {
        amount: prettyFloatMoney(received_verification_bonus_amount),
      }),
    } : null;
  }

  if (verification_bonus_unlock_remain_amount > 0) {
    return {
      title: t('bonus_for_verification.title'),
      badge: t('bonus_for_verification.badge_ready'),
      desc: t('bonus_for_verification.desc_invest', {
        amount: prettyFloatMoney(verification_bonus_min_investment_amount),
        remainingAmount: prettyFloatMoney(
          verification_bonus_unlock_remain_amount,
        ),
      }),
    };
  }

  return null;
};

export const useBonusForVerification = () => {
  const { t } = useTranslation('common');
  const { ready, veriffCode, isVeriffPassed } = useVeriffStatus();
  const { data: investingStatus } = useGetInvestingStatus();
  const { openSingleModal } = useModal();

  // One time modal for newcomers for success verification
  useEffect(() => {
    if (!investingStatus || !veriffCode) return;
    if (!isVeriffPassed) return;
    if (investingStatus.has_previously_invested || investingStatus.is_participating) return;
    if (!Number(investingStatus.received_verification_bonus_amount)) return;

    if (!localStorage.getItem(LOCAL_STORAGE_KEY)) {
      openSingleModal(MODAL_IDS.verification_success_modal);
    }
  }, [investingStatus, veriffCode, isVeriffPassed, openSingleModal]);

  const props = useMemo(
    () => getBonusProps({ ready, veriffCode, investingStatus, t }),
    [ready, veriffCode, investingStatus, t],
  );

  if (!props) return null;

  return <BonusForVerification data={props} />;
};
